import React, { useState, useMemo } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useTransactions, useProfile } from '../hooks/useData';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';

const categoryColors = {
  'Rent': '#3B82F6',
  'Groceries': '#10B981',
  'Dining Out': '#EC4899',
  'Gas': '#F59E0B',
  'Auto Loan': '#84CC16',
  'Student Loan': '#EF4444',
  'Utilities': '#6B7280',
  'Phone': '#8B5CF6',
  'Internet': '#06B6D4',
  'Subscriptions': '#8B5CF6',
  'Shopping': '#F97316',
  'Entertainment': '#F97316',
  'Travel': '#14B8A6',
  'Savings': '#4F46E5',
  'Investments': '#06B6D4',
  'Salary': '#10B981',
  'Transfer': '#6B7280',
  'Auto Insurance': '#84CC16',
};

const categoryIcons = {
  'Rent': '🏠',
  'Groceries': '🛒',
  'Dining Out': '🍽️',
  'Gas': '⛽',
  'Auto Loan': '🚗',
  'Student Loan': '🎓',
  'Utilities': '💡',
  'Phone': '📱',
  'Internet': '🌐',
  'Subscriptions': '📺',
  'Shopping': '🛍️',
  'Entertainment': '🎬',
  'Travel': '✈️',
  'Savings': '💰',
  'Investments': '📈',
  'Salary': '💰',
  'Transfer': '↔️',
  'Auto Insurance': '🚗',
};

const Spending = () => {
  const { data: transactions, loading: transactionsLoading, error: transactionsError, refresh: refreshTransactions } = useTransactions();
  const { data: profile, loading: profileLoading, error: profileError, refresh: refreshProfile } = useProfile();
  const [selectedMonth, setSelectedMonth] = useState('2025-06');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const loading = transactionsLoading || profileLoading;
  const error = transactionsError || profileError;
  const refresh = () => {
    refreshTransactions();
    refreshProfile();
  };

  // Calculate spending analytics
  const spendingAnalytics = useMemo(() => {
    if (!transactions || !profile) return null;

    // Filter to spending transactions in selected month
    const monthlySpending = transactions.filter(tx => 
      tx.date.startsWith(selectedMonth) && tx.amount < 0
    );

    // Group by category
    const byCategory = {};
    monthlySpending.forEach(tx => {
      const category = tx.category;
      if (!byCategory[category]) {
        byCategory[category] = {
          name: category,
          amount: 0,
          transactions: [],
          budgeted: profile.monthly_spending[category.toLowerCase().replace(/\s+/g, '_')] || 0
        };
      }
      byCategory[category].amount += Math.abs(tx.amount);
      byCategory[category].transactions.push(tx);
    });

    // Convert to array and sort
    const categoryArray = Object.values(byCategory).sort((a, b) => b.amount - a.amount);
    
    // Calculate totals
    const totalSpent = categoryArray.reduce((sum, cat) => sum + cat.amount, 0);
    const totalBudgeted = Object.values(profile.monthly_spending).reduce((sum, val) => sum + val, 0);
    
    // Add percentages
    categoryArray.forEach(cat => {
      cat.percentage = totalSpent > 0 ? Math.round((cat.amount / totalSpent) * 100) : 0;
      cat.budgetVariance = cat.amount - cat.budgeted;
      cat.budgetPercentUsed = cat.budgeted > 0 ? Math.round((cat.amount / cat.budgeted) * 100) : 0;
    });

    return {
      categories: categoryArray,
      totalSpent,
      totalBudgeted,
      budgetVariance: totalSpent - totalBudgeted,
      transactionCount: monthlySpending.length
    };
  }, [transactions, profile, selectedMonth]);

  // Get available months
  const availableMonths = useMemo(() => {
    if (!transactions) return [];
    const months = [...new Set(transactions.map(tx => tx.date.substring(0, 7)))].sort().reverse();
    return months;
  }, [transactions]);

  // Filter transactions for display
  const filteredTransactions = useMemo(() => {
    if (!transactions || !spendingAnalytics) return [];
    
    let filtered = transactions.filter(tx => 
      tx.date.startsWith(selectedMonth) && tx.amount < 0
    );

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(tx => tx.category === selectedCategory);
    }

    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [transactions, selectedMonth, selectedCategory]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <View style={styles.tooltip}>
          <Text style={styles.tooltipCategory}>{data.name}</Text>
          <Text style={styles.tooltipAmount}>${data.amount.toLocaleString()}</Text>
          <Text style={styles.tooltipPercentage}>{data.percentage}% of total</Text>
        </View>
      );
    }
    return null;
  };

  if (loading) return <LoadingState message="Loading spending data..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!transactions || !profile || !spendingAnalytics) return null;

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Spending Analysis</Text>
        <View style={styles.monthSelector}>
          <Text style={styles.monthLabel}>Month:</Text>
          {availableMonths.map(month => (
            <TouchableOpacity
              key={month}
              style={[
                styles.monthButton,
                selectedMonth === month ? styles.monthButtonActive : styles.monthButtonInactive
              ]}
              onPress={() => setSelectedMonth(month)}
            >
              <Text style={[
                styles.monthButtonText,
                selectedMonth === month ? styles.monthButtonTextActive : styles.monthButtonTextInactive
              ]}>
                {formatMonth(month)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Summary Cards */}
      <View style={styles.summaryGrid}>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>Total Spent</Text>
          <Text style={styles.spentAmount}>${spendingAnalytics.totalSpent.toLocaleString()}</Text>
          <Text style={styles.summarySubtext}>{spendingAnalytics.transactionCount} transactions</Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>Budgeted</Text>
          <Text style={styles.budgetedAmount}>${spendingAnalytics.totalBudgeted.toLocaleString()}</Text>
          <Text style={styles.summarySubtext}>Monthly budget</Text>
        </View>
        <View style={[
          styles.summaryCard,
          spendingAnalytics.budgetVariance <= 0 ? styles.positiveVariance : styles.negativeVariance
        ]}>
          <Text style={[
            styles.summaryLabel,
            spendingAnalytics.budgetVariance <= 0 ? styles.positiveText : styles.negativeText
          ]}>
            {spendingAnalytics.budgetVariance <= 0 ? 'Under Budget' : 'Over Budget'}
          </Text>
          <Text style={[
            styles.varianceAmount,
            spendingAnalytics.budgetVariance <= 0 ? styles.positiveText : styles.negativeText
          ]}>
            {spendingAnalytics.budgetVariance <= 0 ? '' : '+'}${Math.abs(spendingAnalytics.budgetVariance).toLocaleString()}
          </Text>
          <Text style={[
            styles.summarySubtext,
            spendingAnalytics.budgetVariance <= 0 ? styles.positiveText : styles.negativeText
          ]}>
            {spendingAnalytics.budgetVariance <= 0 ? 'Great job!' : 'Review spending'}
          </Text>
        </View>
      </View>

      {/* Spending Breakdown Chart */}
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>Spending by Category</Text>
        <View style={styles.chartContainer}>
          <View style={styles.pieChartContainer}>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={spendingAnalytics.categories}
                  dataKey="amount"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={20}
                  fill="#8884d8"
                  strokeWidth={2}
                  stroke="#ffffff"
                >
                  {spendingAnalytics.categories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={categoryColors[entry.name] || '#6B7280'} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </View>
          <View style={styles.legendContainer}>
            {spendingAnalytics.categories.slice(0, 8).map((category, index) => (
              <View key={category.name} style={styles.legendItem}>
                <View
                  style={[
                    styles.legendColor,
                    { backgroundColor: categoryColors[category.name] || '#6B7280' }
                  ]}
                />
                <Text style={styles.legendText}>{category.name}</Text>
                <Text style={styles.legendAmount}>${category.amount.toLocaleString()}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>

      {/* Category Breakdown */}
      <View style={styles.categoryCard}>
        <Text style={styles.cardTitle}>Category Details</Text>
        <View style={styles.categoryFilters}>
          <TouchableOpacity
            style={[
              styles.categoryFilter,
              selectedCategory === 'All' ? styles.categoryFilterActive : styles.categoryFilterInactive
            ]}
            onPress={() => setSelectedCategory('All')}
          >
            <Text style={[
              styles.categoryFilterText,
              selectedCategory === 'All' ? styles.categoryFilterTextActive : styles.categoryFilterTextInactive
            ]}>
              All Categories
            </Text>
          </TouchableOpacity>
          {spendingAnalytics.categories.slice(0, 6).map(category => (
            <TouchableOpacity
              key={category.name}
              style={[
                styles.categoryFilter,
                selectedCategory === category.name ? styles.categoryFilterActive : styles.categoryFilterInactive
              ]}
              onPress={() => setSelectedCategory(category.name)}
            >
              <Text style={[
                styles.categoryFilterText,
                selectedCategory === category.name ? styles.categoryFilterTextActive : styles.categoryFilterTextInactive
              ]}>
                {category.name}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <View style={styles.categoryList}>
          {spendingAnalytics.categories.map(category => (
            <View key={category.name} style={styles.categoryItem}>
              <View style={styles.categoryHeader}>
                <View style={styles.categoryInfo}>
                  <Text style={styles.categoryIcon}>{categoryIcons[category.name] || '💳'}</Text>
                  <View style={styles.categoryDetails}>
                    <Text style={styles.categoryName}>{category.name}</Text>
                    <Text style={styles.categoryCount}>{category.transactions.length} transactions</Text>
                  </View>
                </View>
                <View style={styles.categoryAmounts}>
                  <Text style={styles.categorySpent}>${category.amount.toLocaleString()}</Text>
                  <Text style={styles.categoryBudget}>Budget: ${category.budgeted.toLocaleString()}</Text>
                </View>
              </View>
              
              {/* Budget Progress Bar */}
              <View style={styles.progressBarContainer}>
                <View style={styles.progressBarBackground}>
                  <View 
                    style={[
                      styles.progressBarFill,
                      { 
                        width: `${Math.min(category.budgetPercentUsed, 100)}%`,
                        backgroundColor: category.budgetPercentUsed > 100 ? '#EF4444' : 
                                       category.budgetPercentUsed > 90 ? '#F59E0B' : '#10B981'
                      }
                    ]}
                  />
                </View>
                <Text style={[
                  styles.progressText,
                  { color: category.budgetPercentUsed > 100 ? '#EF4444' : '#6B7280' }
                ]}>
                  {category.budgetPercentUsed}% of budget
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Recent Transactions */}
      <View style={styles.transactionsCard}>
        <Text style={styles.cardTitle}>
          Recent Transactions {selectedCategory !== 'All' ? `(${selectedCategory})` : ''}
        </Text>
        <View style={styles.transactionsList}>
          {filteredTransactions.slice(0, 10).map(transaction => (
            <View key={transaction.id} style={styles.transactionItem}>
              <View style={styles.transactionInfo}>
                <Text style={styles.transactionIcon}>{categoryIcons[transaction.category] || '💳'}</Text>
                <View style={styles.transactionDetails}>
                  <Text style={styles.transactionMerchant}>{transaction.merchant}</Text>
                  <Text style={styles.transactionDate}>{transaction.date}</Text>
                </View>
              </View>
              <View style={styles.transactionAmount}>
                <Text style={styles.transactionAmountText}>
                  ${Math.abs(transaction.amount).toLocaleString()}
                </Text>
                <View style={[
                  styles.categoryBadge,
                  { backgroundColor: `${categoryColors[transaction.category] || '#6B7280'}20` }
                ]}>
                  <Text style={[
                    styles.categoryBadgeText,
                    { color: categoryColors[transaction.category] || '#6B7280' }
                  ]}>
                    {transaction.category}
                  </Text>
                </View>
              </View>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 1024,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 16,
  },
  monthSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  monthLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginRight: 8,
  },
  monthButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
  },
  monthButtonActive: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  monthButtonInactive: {
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
  },
  monthButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  monthButtonTextActive: {
    color: '#FFFFFF',
  },
  monthButtonTextInactive: {
    color: '#374151',
  },

  // Summary Cards
  summaryGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  positiveVariance: {
    backgroundColor: '#F0FDF4',
  },
  negativeVariance: {
    backgroundColor: '#FEF2F2',
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
    marginBottom: 4,
  },
  spentAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#374151',
  },
  budgetedAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2563EB',
  },
  varianceAmount: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  positiveText: {
    color: '#059669',
  },
  negativeText: {
    color: '#DC2626',
  },
  summarySubtext: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 4,
  },

  // Chart Card
  chartCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  chartContainer: {
    flexDirection: 'row',
    gap: 24,
  },
  pieChartContainer: {
    flex: 1,
    height: 250,
  },
  legendContainer: {
    flex: 1,
    paddingLeft: 16,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  legendText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
  },
  legendAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  tooltip: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  tooltipCategory: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  tooltipAmount: {
    fontSize: 14,
    color: '#6B7280',
  },
  tooltipPercentage: {
    fontSize: 12,
    color: '#9CA3AF',
  },

  // Category Card
  categoryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  categoryFilters: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  categoryFilter: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
  },
  categoryFilterActive: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  categoryFilterInactive: {
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
  },
  categoryFilterText: {
    fontSize: 14,
    fontWeight: '500',
  },
  categoryFilterTextActive: {
    color: '#FFFFFF',
  },
  categoryFilterTextInactive: {
    color: '#374151',
  },
  categoryList: {
    gap: 16,
  },
  categoryItem: {
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    paddingBottom: 16,
  },
  categoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  categoryInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  categoryIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  categoryDetails: {
    flex: 1,
  },
  categoryName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  categoryCount: {
    fontSize: 12,
    color: '#6B7280',
  },
  categoryAmounts: {
    alignItems: 'flex-end',
  },
  categorySpent: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  categoryBudget: {
    fontSize: 12,
    color: '#6B7280',
  },
  progressBarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  progressBarBackground: {
    flex: 1,
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 12,
    fontWeight: '500',
    minWidth: 80,
    textAlign: 'right',
  },

  // Transactions Card
  transactionsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  transactionsList: {
    gap: 12,
  },
  transactionItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  transactionInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  transactionIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  transactionDetails: {
    flex: 1,
  },
  transactionMerchant: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  transactionDate: {
    fontSize: 12,
    color: '#6B7280',
  },
  transactionAmount: {
    alignItems: 'flex-end',
  },
  transactionAmountText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 2,
  },
  categoryBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  categoryBadgeText: {
    fontSize: 10,
    fontWeight: '500',
  },
});

export default Spending; 